/**
 * What an SMS costs to send — a port of the service's `utils/smsSegments.js`,
 * and it must stay one.
 *
 * It is mirrored rather than fetched for the same reason `dateFormat.ts` is: the
 * counter has to move as the operator types, and a round trip per keystroke is
 * not a counter. The service still prices every send with its own copy and
 * refuses copy that is too long, so a drift between the two shows up as a
 * campaign the builder said was fine and the send path turned down — which is
 * why `smsSegments.test.ts` pins the same cases the service's verification
 * script does, character for character.
 *
 * The two rules worth knowing, because neither is visible in a composer:
 *
 *  - The whole message picks ONE encoding. Any character outside GSM-7 forces
 *    the entire body to UCS-2, where a segment holds 70 instead of 160 — so one
 *    curly apostrophe pasted out of Word halves the capacity of the message it
 *    appears in and doubles what the tenant pays for it.
 *  - Concatenation is not free. A multi-segment message spends part of each
 *    segment on the header that reassembles it, so the second segment does not
 *    start at 161: the whole message re-packs at 153 (or 67).
 */

const GSM_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
/** ESC + the character: two septets each, and the pair can never be split. */
const GSM_EXTENDED = '\f^{}\\[~]|€';

const BASIC = new Set(GSM_BASIC);
const EXTENDED = new Set(GSM_EXTENDED);

export const GSM_SINGLE = 160;
export const GSM_CONCAT = 153;
export const UCS2_SINGLE = 70;
export const UCS2_CONCAT = 67;

/**
 * How long a campaign's text body may be, mirroring the service's own
 * `CAMPAIGN_MAX_SEGMENTS` — the number the Send button enforces. It lives beside
 * the counter rather than in the screen that draws it, because three readers now
 * quote it (the composer, the AI assist hint, the pace card) and a ceiling each
 * of them spelled separately is one that drifts.
 */
export const CAMPAIGN_MAX_SEGMENTS = 6;

export interface SmsCost {
  encoding: 'GSM-7' | 'UCS-2';
  /** What a human counts: code points, so one emoji is one. */
  characters: number;
  /** What the encoding counts — the two differ on exactly the surprising cases. */
  units: number;
  segments: number;
  perSegment: number;
  remaining: number;
  /** Only ever populated on UCS-2, and it is the whole explanation. */
  offenders: string[];
}

export function isGsm(text: string): boolean {
  for (const character of text ?? '') {
    if (!BASIC.has(character) && !EXTENDED.has(character)) return false;
  }
  return true;
}

export function nonGsmCharacters(text: string): string[] {
  const seen = new Set<string>();
  for (const character of text ?? '') {
    if (BASIC.has(character) || EXTENDED.has(character)) continue;
    seen.add(character);
  }
  return [...seen];
}

/**
 * A greedy fill rather than `ceil(units / perSegment)`, because the two disagree
 * exactly where it matters: an ESC pair or a surrogate pair straddling a
 * boundary has to move whole into the next segment, which can push the message
 * onto an extra one the division says is not there. Undercounting is the
 * dangerous direction — it is what would show an operator a cost lower than
 * their bill.
 */
function packSegments(units: number[], perSegment: number): number {
  if (!units.length) return 1;
  let segments = 1;
  let used = 0;
  for (const cost of units) {
    if (used + cost > perSegment) {
      segments += 1;
      used = cost;
    } else {
      used += cost;
    }
  }
  return segments;
}

function unitsFor(text: string, gsm: boolean): number[] {
  const units: number[] = [];
  for (const character of text ?? '') {
    // UCS-2 counts UTF-16 code units, so an astral character (an emoji, most
    // flags) is one indivisible unit of two.
    units.push(gsm ? (EXTENDED.has(character) ? 2 : 1) : character.length);
  }
  return units;
}

export function segmentsFor(text: string): SmsCost {
  const body = text ?? '';
  const gsm = isGsm(body);
  const units = unitsFor(body, gsm);
  const total = units.reduce((sum, cost) => sum + cost, 0);
  const single = gsm ? GSM_SINGLE : UCS2_SINGLE;
  const concat = gsm ? GSM_CONCAT : UCS2_CONCAT;
  // A body that fits a lone segment never pays for the concatenation header, so
  // the two sizes cannot be collapsed into one pass.
  const segments = total <= single ? 1 : packSegments(units, concat);
  const perSegment = segments === 1 ? single : concat;
  return {
    encoding: gsm ? 'GSM-7' : 'UCS-2',
    characters: [...body].length,
    units: total,
    segments,
    perSegment,
    remaining: Math.max(0, (segments === 1 ? single : concat * segments) - total),
    offenders: gsm ? [] : nonGsmCharacters(body),
  };
}

/**
 * The typographic characters that are NOT in the GSM alphabet, beside the ASCII
 * ones that are — the server's `GSM_SUBSTITUTIONS` (utils/smsSegments.js),
 * mirrored like the rest of this file. Nothing here changes a word: an accented
 * name, a currency symbol or an emoji is deliberately absent, because silently
 * rewriting those is a different and much worse thing than saving a segment.
 */
const GSM_SUBSTITUTIONS: [RegExp, string][] = [
  [/[\u2018\u2019\u201A\u201B]/g, "'"],
  [/[\u201C\u201D\u201E\u201F]/g, '"'],
  [/[\u2013\u2014\u2015]/g, '-'],
  [/\u2026/g, '...'],
  [/[\u00A0\u2007\u202F]/g, ' '],
];

/**
 * `text` with those substitutions applied. Only ever on a press — the
 * pre-flight review's "Fix it" — never as the operator types: what a person
 * wrote is theirs to spell as they like until they ask.
 */
export function toGsmSafe(text: string): string {
  let out = text ?? '';
  for (const [pattern, replacement] of GSM_SUBSTITUTIONS) out = out.replace(pattern, replacement);
  return out;
}

/**
 * The one sentence a composer shows under the box.
 *
 * It names the encoding only when it is the bad one, because "GSM-7" means
 * nothing to an operator and "Unicode — because of ’" is the entire actionable
 * fact: delete that character and the message halves in cost.
 */
export function smsCostLabel(cost: SmsCost): string {
  const plural = cost.segments === 1 ? '' : 's';
  const base = `${cost.characters} characters · ${cost.segments} segment${plural}`;
  if (cost.encoding === 'GSM-7') return `${base} · ${cost.remaining} left in this one`;
  const shown = cost.offenders.slice(0, 3).join(' ');
  const more = cost.offenders.length > 3 ? '…' : '';
  return `${base} · sent as Unicode because of ${shown}${more}, which halves what fits in each`;
}
